import React from 'react';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ActSchedule from '@/types/ActSchedule.type';
import { Box, Grid, Link } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Stack } from '@mui/system';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import URL from '@/common/routes/Url';
import ActPlanIcon from '@/components/actPlan/ActPlanIcon';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

type Props = {
  actSchedule: ActSchedule;
};

const ActPlanCard: React.FC<Props> = ({ actSchedule }) => {
  const [expanded, setExpanded] = React.useState(false);
  const navigate = useNavigate();

  const nextDate = (undecidedFlag: boolean, date: string | null) => {
    if (undecidedFlag) {
      return '未定';
    } else if (date) {
      return dayjs(date).format('MM/DD');
    }
    return '';
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Card>
      <CardHeader
        avatar={<ActPlanIcon actSchedule={actSchedule} />}
        action={
          <IconButton
            sx={{ marginRight: '7.5px' }}
            onClick={() => {
              navigate(`${URL.plan}/${actSchedule.id}`);
            }}
          >
            <EditIcon />
          </IconButton>
        }
        title={actSchedule.employee.name}
        subheader={actSchedule.status.displayName + '：' + actSchedule.style.name}
      />
      <CardActions sx={{ paddingTop: 0, paddingLeft: '16px', paddingRight: '16px' }}>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <Stack spacing={0}>
              <Typography variant="subtitle2" gutterBottom sx={{ marginBottom: 0 }}>
                戻り時間
              </Typography>
              <Typography
                variant="body2"
                gutterBottom
                sx={{
                  ...{ color: '#666666', marginTop: 0 },
                  ...(!actSchedule.goingStraightHomeFlag &&
                  actSchedule.returnTime &&
                  dayjs().format('HH:mm') > actSchedule.returnTime
                    ? { color: '#ff8a80' }
                    : {}),
                }}
              >
                {actSchedule.goingStraightHomeFlag ? '直帰' : actSchedule.returnTime}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={5}>
            <Stack spacing={0}>
              <Typography variant="subtitle2" gutterBottom sx={{ marginBottom: 0 }}>
                次回出社日
              </Typography>
              <Typography
                variant="body2"
                gutterBottom
                sx={{
                  ...{ color: '#666666', marginTop: 0 },
                  ...(!actSchedule.undecidedFlag && actSchedule.nextDate && dayjs() > dayjs(actSchedule.nextDate)
                    ? { color: '#ff8a80' }
                    : {}),
                }}
              >
                {nextDate(actSchedule.undecidedFlag, actSchedule.nextDate)}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={2} display={'flex'} justifyContent={'end'} alignItems={'flex-end'}>
            <ExpandMore expand={expanded} onClick={handleExpandClick} aria-expanded={expanded} aria-label="show more">
              <ExpandMoreIcon />
            </ExpandMore>
          </Grid>
        </Grid>
      </CardActions>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardActions sx={{ paddingTop: 0, paddingLeft: '16px', paddingRight: '16px' }}>
          <Grid container spacing={2}>
            <Grid item xs={5}>
              <Stack spacing={0}>
                <Typography variant="subtitle2" gutterBottom sx={{ marginBottom: 0 }}>
                  連絡先
                </Typography>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{
                    ...{ color: '#666666', marginTop: 0 },
                  }}
                >
                  {actSchedule.phone ? (
                    <Link href={'tel:' + actSchedule.phone.number} underline="hover">
                      {actSchedule.phone.number}
                    </Link>
                  ) : (
                    <Stack>
                      <Box>{actSchedule.employee.extensionNumber}</Box>
                      <Box>
                        <Link href={'tel:' + actSchedule.employee.phoneNumber} underline="hover">
                          {actSchedule.employee.phoneNumber}
                        </Link>
                      </Box>
                    </Stack>
                  )}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={7}>
              <Stack spacing={0}>
                <Typography variant="subtitle2" gutterBottom sx={{ marginBottom: 0 }}>
                  行先
                </Typography>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{
                    ...{ color: '#666666', marginTop: 0 },
                  }}
                >
                  {actSchedule.place}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </CardActions>
      </Collapse>
    </Card>
  );
};
export default ActPlanCard;
